/**
 * The stripped prologue's readable half — #22, docs/08 §2.
 *
 * The idea is SotN's: hand the player everything, let them enjoy it, then take
 * it. docs/08 §2 argues it is worth *more* to a roguelite than it was there,
 * because our onboarding problem is 29 weapon classes, 84 materials and nine
 * traversal abilities meeting a player with no idea which matter. Four minutes
 * of holding all of it gives them first-hand memory of the whole verb set, and
 * every later unlock becomes a recovery rather than an introduction.
 *
 * This file owns exactly one thing: **what the player reads**. The chamber is
 * world/'s and the script is the integrator's; the rule here is:
 *
 *   **Teach with a key, not a sentence.** A beat that can be taught by showing
 *   a key cap and a verb next to the thing it acts on shows that and nothing
 *   else, and *removes it the instant the player does it*. The disappearance is
 *   the feedback. Prose is reserved for the one beat that is not a verb — the
 *   strip itself, which is a narrative event and has nothing to demonstrate.
 *
 * `PROLOGUE_BEATS` is the authored script; the integrator advances through it
 * and hands back a `PrologueVM` each frame. Nothing here is derived from the
 * simulation, so the script never touches `uiAdapter` — `UIRoot.setPrologue` is
 * the seam. The *key caps* are the one thing on this screen that is not
 * authored, and they arrive by the other road, with the frame: see below.
 *
 * **The key caps are not authored — #135.** They were, and on the one screen
 * whose entire job is teaching buttons: `'Space'`, `'Shift'`, `'J'`, `'L'`,
 * `'E'`, written as literals next to the verb. A player who had moved jump in
 * Settings was taught a key that does not jump, and the two spellings did not
 * even match the rest of the interface — `keyLabel('ShiftLeft')` prints
 * *"Left Shift"*. Same defect class as #129 and #134: **a control label that is
 * authored rather than resolved will drift from the control.** A beat now names
 * the *action* and the overlay prints whatever the live table says that action
 * is on, through `bindingRows` — the one speller the settings screen, the title
 * legend and the in-run hint line already share.
 *
 * The one exception is honest and explicit: movement. `left` and `right` are
 * deliberately absent from `REBINDABLE` (each holds WASD *and* an arrow, and
 * rebinding one would silently cost the other), so A/D cannot move and a
 * literal is the truth rather than a shortcut. `PrologueCap.action === null`
 * says so in the type, rather than leaving it to be noticed.
 */

import { keyLabel } from './settingsModel';
import { clear, el, Pool, TextBind } from './style';
import type { KeyLabelsVM } from './viewModels';

/**
 * What a beat exists to teach, or null when it exists to say something.
 *
 * #22's acceptance criteria name four things the prologue must cover — movement,
 * one traversal ability, one weapon, one material interaction — and this is what
 * makes "covers them" checkable rather than a matter of opinion.
 */
export type PrologueLesson = 'movement' | 'traversal' | 'weapon' | 'material';

/**
 * One key cap in a prompt: either an action the player can move, or a key that
 * cannot be moved at all.
 *
 * There is no third case, and that is the point of writing it as a pair rather
 * than as a string. An authored cap for something that *is* rebindable is the
 * #135 defect; a resolved cap for something that is not would be a lookup into
 * a table `left` and `right` are deliberately not in, which resolves to
 * "unbound" and teaches nobody anything.
 */
export interface PrologueCap {
  /**
   * The action id this cap prints the live binding of — an `Action` from
   * `core/input.ts`, held as a plain string for the same reason
   * `RebindDef.action` is (this file imports nothing from `src/core`).
   *
   * `null` means the key is not rebindable, and `fixed` is then the truth.
   */
  action: string | null;
  /** The literal cap. Read only when `action` is null; empty otherwise. */
  fixed: string;
}

/** A cap the player can move: printed from the table the game is played on. */
function bound(action: string): PrologueCap {
  return { action, fixed: '' };
}

/**
 * A cap the player cannot move.
 *
 * Movement, and nothing else. `tests/prologueKeys.test.ts` asserts every fixed
 * cap in the script names a key that really is absent from `REBINDABLE`, so
 * this cannot quietly become the easy way to write a cap.
 */
function fixed(label: string): PrologueCap {
  return { action: null, fixed: label };
}

export interface ProloguePrompt {
  /** Key caps, left to right, as the player will see them on the keyboard. */
  caps: readonly PrologueCap[];
  /** One word. A verb needing a sentence is a beat that has not been designed. */
  verb: string;
}

/**
 * What a cap prints, given the labels the game is actually being played on.
 *
 * `keys` comes from `GameVM.keyLabels`, which `UIAdapter.setBindings` fills
 * from `bindingRows(table, {})` — so a cap here and a row in Settings cannot
 * describe the same key differently, including the awkward case of an action
 * with no key at all, which both call `unbound`.
 */
export function prologueCapLabel(cap: PrologueCap, keys: KeyLabelsVM): string {
  if (cap.action === null) return cap.fixed;
  // `keyLabel(null)` rather than a literal: "unbound" is spelt once, in the
  // same function the settings rows spell it with.
  return keys.get(cap.action) ?? keyLabel(null);
}

export interface PrologueBeat {
  id: string;
  /**
   * Authored line, or null. Null is the expected value: a beat with a prompt
   * has already taught its lesson and a card would only be describing what the
   * player is doing.
   */
  card: string | null;
  cardNote: string | null;
  prompts: readonly ProloguePrompt[];
  teaches: PrologueLesson | null;
}

/** A card longer than this is prose, and prose is what this design avoids. */
export const PROLOGUE_CARD_WORD_CAP = 10;

/** Hold, never tap: a tap-skip on a first run is an accident, not a choice. */
export const PROLOGUE_SKIP_HOLD_MS = 900;

/**
 * The script.
 *
 * Six verbs and one sentence. The verbs are in the order the chamber presents
 * them, and every one of them is a key the player will use for the rest of the
 * game — the prologue is not a tutorial level with its own vocabulary. Which is
 * exactly why five of the six are `bound(...)`: the key the prologue teaches
 * has to be the key the rest of the game is played on, and the only way to
 * guarantee that is to not write it down here.
 */
export const PROLOGUE_BEATS: readonly PrologueBeat[] = [
  // You wake holding everything. Nothing says so; the HUD is simply full.
  { id: 'wake', card: null, cardNote: null, teaches: 'movement', prompts: [{ caps: [fixed('A'), fixed('D')], verb: 'move' }] },
  { id: 'gap', card: null, cardNote: null, teaches: 'movement', prompts: [{ caps: [bound('jump')], verb: 'jump' }] },
  { id: 'shelf', card: null, cardNote: null, teaches: 'movement', prompts: [{ caps: [bound('dash')], verb: 'dash' }] },
  // Oil underfoot and a lit blade. Nobody explains the fire; the fire explains
  // itself, which is the entire argument of docs/03 §7.
  { id: 'slick', card: null, cardNote: null, teaches: 'material', prompts: [{ caps: [bound('mainAttack')], verb: 'strike' }] },
  { id: 'seam', card: null, cardNote: null, teaches: 'traversal', prompts: [{ caps: [bound('ability')], verb: 'breach' }] },
  { id: 'plinth', card: null, cardNote: null, teaches: 'weapon', prompts: [{ caps: [bound('interact')], verb: 'take' }] },
  // The one beat with nothing to demonstrate. It is a loss, not a verb.
  {
    id: 'strip',
    card: 'It takes all of it.',
    cardNote: 'You will earn each piece back.',
    teaches: null,
    prompts: [],
  },
  // Ends by handing the world back with the standard kit. The last thing on
  // screen is a key, so the prologue closes the way it opened.
  { id: 'surface', card: null, cardNote: null, teaches: 'movement', prompts: [{ caps: [fixed('A'), fixed('D')], verb: 'go on' }] },
];

export const PROLOGUE_BEAT_IDS: readonly string[] = PROLOGUE_BEATS.map((b) => b.id);

export interface ProloguePromptVM extends ProloguePrompt {
  /**
   * 0..1 — how much of this lesson the player has done. The prompt fades as it
   * fills and is gone at 1, so a player who already knows the verb never has a
   * prompt on screen for longer than the action takes.
   */
  progress01: number;
}

export interface PrologueVM {
  /** Which beat, so the overlay can rebuild only when the script advances. */
  beat: string;
  card: string | null;
  cardNote: string | null;
  prompts: readonly ProloguePromptVM[];
  /**
   * Whether skipping is offered at all. False on a profile's first run: the
   * prologue is the onboarding, and offering to skip onboarding to someone who
   * has never played is offering them the worst option on the screen.
   */
  skippable: boolean;
  /** 0..1 of `PROLOGUE_SKIP_HOLD_MS`. */
  skipHeld01: number;
}

/**
 * The key cap the skip hint names. One place, so the hint cannot drift.
 *
 * Authored, and legitimately: `Escape` is in `RESERVED_CODES` — `ui/uiRoot.ts`
 * reads it by literal `e.code` as well as `Input`, so the settings screen
 * refuses to bind anything onto it and offers no row for `pause`. There is no
 * table to resolve through and no rebinding that could make this wrong.
 * `tests/prologueKeys.test.ts` pins that premise: if Escape ever becomes
 * movable, this literal is the next #135.
 */
export const PROLOGUE_SKIP_KEY = 'Esc';

/**
 * Where the prologue advances to when it is skipped or when it ends.
 *
 * Exported because the integrator needs the same answer for both paths and #22
 * requires that neither leaks prologue gear — one named terminus is easier to
 * keep honest than two call sites that each remember to strip.
 */
export const PROLOGUE_END_BEAT = 'surface';

export class PrologueOverlay {
  readonly root: HTMLElement;

  private cardEl: HTMLElement;
  private cardBind: TextBind;
  private noteBind: TextBind;
  private promptHost: HTMLElement;
  private promptPool: Pool<HTMLElement>;
  private skipEl: HTMLElement;
  private skipFill: HTMLElement;

  private lastBeat = ' ';

  constructor() {
    this.root = el('div', 'pro');
    this.root.hidden = true;

    this.cardEl = el('div', 'pro__card');
    const line = el('div', 'pro__line');
    const note = el('div', 'pro__note');
    this.cardEl.append(line, note);
    this.cardBind = new TextBind(line);
    this.noteBind = new TextBind(note);

    this.promptHost = el('div', 'pro__prompts');
    this.promptPool = new Pool(this.promptHost, () => {
      const p = el('div', 'pro__prompt');
      p.append(el('span', 'pro__keys'), el('span', 'pro__verb'));
      return p;
    });

    this.skipEl = el('div', 'pro__skip');
    this.skipFill = el('i');
    this.skipEl.append(document.createTextNode('hold ' + PROLOGUE_SKIP_KEY + ' to skip'), this.skipFill);

    this.root.append(this.cardEl, this.promptHost, this.skipEl);
  }

  /**
   * Draw a frame of the prologue.
   *
   * `keys` is `GameVM.keyLabels` — the labels the game is being played on, not
   * the shipped defaults — and it is a required argument rather than an
   * optional one on purpose: a default would be a second answer to "what key is
   * this?", and #135 is what happens when there are two.
   */
  update(vm: PrologueVM | null, keys: KeyLabelsVM): void {
    if (!vm) {
      if (!this.root.hidden) {
        this.root.hidden = true;
        this.lastBeat = ' ';
        // Leave nothing rendered behind the hidden overlay: the prologue runs
        // once and the next thing to use this element is the next profile.
        clear(this.promptHost);
        this.promptPool.dispose();
      }
      return;
    }
    if (this.root.hidden) this.root.hidden = false;

    if (vm.beat !== this.lastBeat) {
      this.lastBeat = vm.beat;
      // A data attribute rather than a class per beat: the stylesheet animates
      // the card in on change, and one attribute flip is the whole cost.
      this.root.dataset.beat = vm.beat;
    }

    this.cardBind.set(vm.card ?? '');
    this.noteBind.set(vm.cardNote ?? '');
    if (this.cardEl.hidden === (vm.card !== null)) this.cardEl.hidden = vm.card === null;

    this.promptPool.begin();
    for (const p of vm.prompts) {
      const e = this.promptPool.next();
      const capHost = e.children[0] as HTMLElement;
      const labels = p.caps.map((c) => prologueCapLabel(c, keys));
      // Keyed on the *resolved* text, never on the beat or the action. #134
      // cached a key hint against an id one file over and the cap then froze on
      // whatever it was first drawn with — indistinguishable from a fix until
      // the player rebinds a second time. Rebuilding when the text changes is
      // both the cheap check and the correct one.
      const text = labels.join(' ');
      if (capHost.dataset.k !== text) {
        capHost.dataset.k = text;
        clear(capHost);
        for (const k of labels) capHost.append(el('kbd', '', k));
      }
      const verb = e.children[1] as HTMLElement;
      if (verb.textContent !== p.verb) verb.textContent = p.verb;
      // Fades out as it is learned rather than vanishing on a threshold: the
      // prompt going quiet *is* the confirmation that the input landed.
      const op = (1 - clamp01(p.progress01)).toFixed(2);
      if (e.style.opacity !== op) e.style.opacity = op;
    }
    this.promptPool.end();

    if (this.skipEl.hidden === vm.skippable) this.skipEl.hidden = !vm.skippable;
    if (vm.skippable) this.skipFill.style.setProperty('--f', String(clamp01(vm.skipHeld01)));
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** The beat after `id`, or null at the end. The script is linear by design. */
export function nextPrologueBeat(id: string): PrologueBeat | null {
  const i = PROLOGUE_BEATS.findIndex((b) => b.id === id);
  if (i < 0 || i + 1 >= PROLOGUE_BEATS.length) return null;
  return PROLOGUE_BEATS[i + 1];
}

export function prologueBeat(id: string): PrologueBeat | null {
  return PROLOGUE_BEATS.find((b) => b.id === id) ?? null;
}

/**
 * Build the frame's view model from a beat id and how far through it the player
 * is. Keeps the integrator's job to "which beat, how far", which is the only
 * part of this that is game state.
 */
export function prologueVM(
  beatId: string,
  progress01: number,
  skippable: boolean,
  skipHeld01 = 0,
): PrologueVM | null {
  const b = prologueBeat(beatId);
  if (!b) return null;
  return {
    beat: b.id,
    card: b.card,
    cardNote: b.cardNote,
    prompts: b.prompts.map((p) => ({ ...p, progress01: clamp01(progress01) })),
    skippable,
    skipHeld01: clamp01(skipHeld01),
  };
}
